import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import { useNavigate, Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaPlay } from 'react-icons/fa';
import '../styles/BonusesPage.css';
import AuthModal from "./AuthModal";
import PaymentModal from "./PaymentModal";
import ReferralProgram from "./ReferralProgram";
import BonusNavbar from "./BonusNavbar";
import "../styles/ReferralProgram.css";

import { getApiUrl } from "../utils/config";
import { formatBalanceLocale } from "../utils/numberFormat";
const API_URL = getApiUrl();

const BonusesPage = () => {
    const { t } = useTranslation();
    const { isAuth, showNotification } = useAuth();
    const {
        selectedCurrency,
        getCurrentBonusBalance,
        fetchBalance,
        fiatRates,
        isBalanceLoaded,
        isCurrencyLoaded
    } = usePayment();

    const navigate = useNavigate();

    // Состояния для промокодов
    const [promoCode, setPromoCode] = useState('');
    const [activatedPromoCodes, setActivatedPromoCodes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Состояния для фриспинов
    const [availableGames, setAvailableGames] = useState([]);
    const [activeFreespins, setActiveFreespins] = useState([]);
    const [selectedGame, setSelectedGame] = useState('');
    const [freespinAmount, setFreespinAmount] = useState(10);
    const [userFreespins, setUserFreespins] = useState(0);
    const [freespinLoading, setFreespinLoading] = useState(false);
    const [welcomePromocodes, setWelcomePromocodes] = useState([]);
    const [currentGameIndex, setCurrentGameIndex] = useState(0);
    const [hoveredFreespin, setHoveredFreespin] = useState(null);

    // Состояния для недельного кешбека
    const [cashbackData, setCashbackData] = useState(null);
    const [cashbackLoading, setCashbackLoading] = useState(false);
    const [timerToNextCashback, setTimerToNextCashback] = useState('');

    // Состояния для бонусного кешбека
    const [bonusCashbackData, setBonusCashbackData] = useState(null);
    const [bonusLoading, setBonusLoading] = useState(false);
    const [timerToNextBonus, setTimerToNextBonus] = useState('');
    const [collecting, setCollecting] = useState(false);

    // Состояния для рейкбека
    const [rakebackData, setRakebackData] = useState(null);
    const [rakebackLoading, setRakebackLoading] = useState(false);
    const [timerToNextRakeback, setTimerToNextRakeback] = useState('');
    const [collectingRakeback, setCollectingRakeback] = useState(false);

    // Состояния для инфо-окон
    const [showDailyInfo, setShowDailyInfo] = useState(false);
    const [showWeeklyInfo, setShowWeeklyInfo] = useState(false);

    // Состояние для модалки авторизации
    const [showAuthModal, setShowAuthModal] = useState(false);

    // Состояние для модального окна с информацией о бонусе +500%
    const [showBonusInfoModal, setShowBonusInfoModal] = useState(false);

    // Состояние для модального окна пополнения
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Состояние для модального окна "Кэшбек и бонусы"
    const [showCashbackInfoModal, setShowCashbackInfoModal] = useState(false);

    // Состояние для модального окна "Реферальная программа"
    const [showReferralModal, setShowReferralModal] = useState(false);

    // Ref'ы для навигации к секциям
    const bonusesRef = useRef(null);
    const bonusesInfoRef = useRef(null); // Альтернативный ref для неавторизованных
    const welcomeBonusesRef = useRef(null);
    const bonus500Ref = useRef(null);
    const cashbackRef = useRef(null);
    const referralRef = useRef(null);
    const activeFreespinsSliderRef = useRef(null);

    // Для неавторизованных пользователей используем RUB
    const currentCurrency = isAuth ? selectedCurrency : 'RUB';
    const currentFiatRates = isAuth ? fiatRates : {};

    // Загрузка данных при авторизации - только после полной загрузки PaymentContext
    useEffect(() => {
        if (isAuth && isBalanceLoaded && isCurrencyLoaded) {
            fetchActivatedPromoCodes();
            fetchAvailableGames();
            fetchActiveFreespins();
            fetchUserBalance();
            fetchWelcomePromocodes();
            fetchCashbackData();
            fetchBonusCashbackData();
            fetchRakebackData();
        }
    }, [isAuth, isBalanceLoaded, isCurrencyLoaded]);



    // Обновление currentGameIndex при изменении списка игр
    useEffect(() => {
        if (availableGames.length > 0) {
            // Проверяем, что currentGameIndex не выходит за пределы массива
            if (currentGameIndex >= availableGames.length) {
                setCurrentGameIndex(0);
                setSelectedGame(availableGames[0].uuid);
            } else if (selectedGame !== availableGames[currentGameIndex].uuid) {
                setSelectedGame(availableGames[currentGameIndex].uuid);
            }
        }
    }, [availableGames, currentGameIndex, selectedGame]);

    // Обновление данных при смене валюты - только если PaymentContext готов
    useEffect(() => {
        if (isAuth && selectedCurrency && isBalanceLoaded && isCurrencyLoaded) {
            fetchCashbackData();
            fetchBonusCashbackData();
            fetchRakebackData();
            // Обновляем данные о ставках для фриспинов при смене валюты
            if (availableGames.length > 0) {
                fetchAvailableGames();
            }
        }
    }, [selectedCurrency, isBalanceLoaded, isCurrencyLoaded]);

    // Анимация чисел
    function useAnimatedNumber(target, duration = 1000) {
        const [value, setValue] = useState(0);
        const raf = useRef();

        useEffect(() => {
            let start = null;
            let from = value;
            let to = target;

            function animate(ts) {
                if (!start) start = ts;
                const progress = Math.min((ts - start) / duration, 1);
                setValue(Number((from + (to - from) * progress).toFixed(2)));
                if (progress < 1) {
                    raf.current = requestAnimationFrame(animate);
                } else {
                    setValue(Number(to.toFixed(2)));
                }
            }

            raf.current && cancelAnimationFrame(raf.current);
            raf.current = requestAnimationFrame(animate);

            return () => raf.current && cancelAnimationFrame(raf.current);
        }, [target, duration]);

        return value;
    }

    // Получение символа валюты
    function getCurrencySymbol(currency) {
        switch (currency) {
            case 'USD': return '$';
            case 'RUB': return '₽';
            case 'BRL': return 'R$';
            case 'USDT': return '₮';
            case 'EUR': return '€';
            default: return '';
        }
    }

    // Форматирование числа для отображения (максимум 2 знака после запятой)
    function formatBalanceLocale(value) {
        // Преобразуем в число если это строка
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (numValue === null || numValue === undefined || typeof numValue !== 'number' || isNaN(numValue)) return '0';
        return Number(numValue.toFixed(2)).toLocaleString();
    }

    // Функция для пересчёта RUB в выбранную валюту
    function convertRubFloat(value) {
        if (currentCurrency === 'RUB') return value;
        if (currentCurrency === 'USD' && currentFiatRates.RUB_USD) return value * currentFiatRates.RUB_USD;
        if (currentCurrency === 'BRL' && currentFiatRates.RUB_BRL) return value * currentFiatRates.RUB_BRL;
        return value;
    }

    // Пересчёт RUB в выбранную валюту для отображения
    function convertRub(value) {
        if (currentCurrency === 'RUB') return value;
        if (currentCurrency === 'USD' && currentFiatRates.RUB_USD) return Math.round(value * currentFiatRates.RUB_USD);
        if (currentCurrency === 'BRL' && currentFiatRates.RUB_BRL) return Math.round(value * currentFiatRates.RUB_BRL);
        if (currentCurrency === 'USDT' && currentFiatRates.RUB_USDT) return Math.round(value * currentFiatRates.RUB_USDT);
        if (currentCurrency === 'EUR' && currentFiatRates.RUB_EUR) return Math.round(value * currentFiatRates.RUB_EUR);
        return value;
    }

    // Таблицы для неавторизованных пользователей (в рублях)
    const cashbackTable = useMemo(() => [
        { min: convertRub(50000000), percent: 30, max: convertRub(50000) },
        { min: convertRub(20000000), percent: 20, max: convertRub(30000) },
        { min: convertRub(10000000), percent: 10, max: convertRub(20000) },
        { min: convertRub(1000000), percent: 5, max: convertRub(15000) },
        { min: convertRub(800000), percent: 4, max: convertRub(8000) },
        { min: convertRub(500000), percent: 3, max: convertRub(5000) },
        { min: convertRub(300000), percent: 2, max: convertRub(4000) },
        { min: convertRub(100000), percent: 1, max: convertRub(3000) },
    ], [currentCurrency, currentFiatRates]);

    const bonusCashbackTable = useMemo(() => [
        { min: convertRub(1000000), percent: 20 },
        { min: convertRub(700000), percent: 10 },
        { min: convertRub(500000), percent: 5 },
        { min: convertRub(100000), percent: 4 },
        { min: convertRub(50000), percent: 3 },
        { min: convertRub(20000), percent: 2 },
        { min: convertRub(5000), percent: 1 },
    ], [currentCurrency, currentFiatRates]);

    // Дублирующий блок с велком бонусами для неавторизованных пользователей
    const unauthenticatedWelcomePromocodes = [
        { code: 'WELCOME', status: 'active', bonus_percent: 200, free_spins_count: 50 },
        { code: 'WELCOME2', status: 'active', bonus_percent: 150, free_spins_count: 30 },
        { code: 'WELCOME3', status: 'active', bonus_percent: 100, free_spins_count: 20 },
        { code: 'WELCOME4', status: 'active', bonus_percent: 50, free_spins_count: 10 }
    ];

    // Форматирование таймера из секунд
    function formatTimerFromSeconds(seconds) {
        if (typeof seconds !== 'number' || isNaN(seconds)) return '-';
        const days = Math.floor(seconds / (60 * 60 * 24));
        const hours = Math.floor((seconds / (60 * 60)) % 24);
        const minutes = Math.floor((seconds / 60) % 60);
        const sec = seconds % 60;
        return `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }

    // Получение данных недельного кешбека
    const fetchCashbackData = async () => {
        if (!currentCurrency) return;

        setCashbackLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/weekly-cashback?currency=${currentCurrency}`, {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();
            if (data.success) {
                setCashbackData(data);
            }
        } catch (error) {
            console.error("Ошибка при получении данных кешбека:", error);
        } finally {
            setCashbackLoading(false);
        }
    };

    // Получение данных бонусного кешбека
    const fetchBonusCashbackData = async () => {
        if (!currentCurrency) return;

        setBonusLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/bonus-cashback?currency=${currentCurrency}`, {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();
            if (data.success) {
                setBonusCashbackData(data);
            }
        } catch (error) {
            console.error("Ошибка при получении данных бонусного кешбека:", error);
        } finally {
            setBonusLoading(false);
        }
    };

    // Получение данных рейкбека
    const fetchRakebackData = async () => {
        if (!currentCurrency) return;

        setRakebackLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/rakeback?currency=${currentCurrency}`, {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();
            if (data.success) {
                setRakebackData(data);
            }
        } catch (error) {
            console.error("Ошибка при получении данных рейкбека:", error);
        } finally {
            setRakebackLoading(false);
        }
    };

    // Сбор бонусного кешбека
    const handleCollectBonusCashback = async () => {
        setCollecting(true);
        try {
            const response = await fetch(`${API_URL}/api/collect-bonus-cashback?currency=${currentCurrency}`, {
                method: "POST",
                credentials: "include"
            });
            const data = await response.json();
            if (data.success) {
                await fetchBonusCashbackData();
                await fetchBalance();
                showNotification("Бонус успешно зачислен на основной счёт", "success");
            } else {
                showNotification(data.message || "Ошибка при зачислении бонуса", "error");
            }
        } catch (error) {
            console.error('Ошибка при заборе бонусного кешбека:', error);
            showNotification("Ошибка сервера", "error");
        } finally {
            setCollecting(false);
        }
    };

    // Сбор рейкбека
    const handleCollectRakeback = async () => {
        setCollectingRakeback(true);
        try {
            const response = await fetch(`${API_URL}/api/collect-rakeback?currency=${currentCurrency}`, {
                method: "POST",
                credentials: "include"
            });
            const data = await response.json();
            if (data.success) {
                await fetchRakebackData();
                await fetchBalance();
                showNotification("Рейкбек успешно зачислен на основной счёт", "success");
            } else {
                showNotification(data.message || "Ошибка при зачислении рейкбека", "error");
            }
        } catch (error) {
            console.error('Ошибка при заборе рейкбека:', error);
            showNotification("Ошибка сервера", "error");
        } finally {
            setCollectingRakeback(false);
        }
    };

    // Таймер для автоматического начисления
    useEffect(() => {
        if (cashbackData?.autoPayment && typeof cashbackData?.timeToNextCashback === 'number') {
            setTimerToNextCashback(formatTimerFromSeconds(cashbackData.timeToNextCashback));
            let current = cashbackData.timeToNextCashback;
            const interval = setInterval(() => {
                current -= 1;
                setTimerToNextCashback(formatTimerFromSeconds(current));
                if (current <= 0) clearInterval(interval);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setTimerToNextCashback('');
        }
    }, [cashbackData?.autoPayment, cashbackData?.timeToNextCashback]);

    // Таймер для бонусного кешбека (больше не используется)
    useEffect(() => {
        setTimerToNextBonus(''); // Всегда пустой, так как можно забирать в любой момент
    }, [bonusCashbackData?.timeToNext]);

    // Таймер для рейкбека
    useEffect(() => {
        if (typeof rakebackData?.timeToNext === 'number') {
            let current = rakebackData.timeToNext;
            setTimerToNextRakeback(formatTimerFromSeconds(current));
            const interval = setInterval(() => {
                current -= 1;
                setTimerToNextRakeback(formatTimerFromSeconds(current));
                if (current <= 0) clearInterval(interval);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setTimerToNextRakeback('');
        }
    }, [rakebackData?.timeToNext]);

    // Расчеты для недельного кешбека
    const weeklyTotalBets = cashbackData?.totalBets || 0;
    const weeklyTotalWins = cashbackData?.totalWins || 0;
    const weeklyNetLoss = cashbackData?.netLoss || 0;
    const weeklyPercent = cashbackData?.percent || 0;
    const weeklyCashback = cashbackData?.cashback || 0;
    const weeklyMaxCashback = cashbackData?.maxCashback || 0;


    // Расчеты для бонусного кешбека
    const bonusTotalBets = bonusCashbackData?.totalBets || 0;
    const bonusPercent = bonusCashbackData?.percent || 0;
    const bonusAmount = bonusCashbackData?.cashback || 0;
    const bonusCollectedToday = bonusCashbackData?.collectedBonus || 0;
    const canCollectBonus = bonusCashbackData?.canCollect || false;
    const bonusToBurn = bonusCashbackData?.bonusToBurn || 0;
    const timeToNextCashback = bonusCashbackData?.timeToNextCashback || 0;

    // Расчеты для рейкбека
    const rakebackTotalBets = rakebackData?.totalBets || 0;
    const rakebackTotalWins = rakebackData?.totalWins || 0;
    const rakebackNetLoss = rakebackData?.netLoss || 0;
    const rakebackAmount = rakebackData?.rakeback || 0;
    const canCollectRakeback = rakebackData?.canCollect || false;

    // Анимированные значения убраны - используем прямые значения с форматированием

    // Остальные функции (только для авторизованных пользователей)
    const fetchActivatedPromoCodes = async () => {
        try {
            // Получаем ВСЕ промокоды (включая использованные) для отображения в профиле
            const response = await fetch(`${API_URL}/api/promo/all-user-promos`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                setActivatedPromoCodes(data.allPromoCodes || []);
            }
        } catch (error) {
            console.error('Ошибка при загрузке активированных промокодов:', error);
        }
    };

    const fetchAvailableGames = async () => {
        try {
            const response = await fetch(`${API_URL}/api/user-freespins/games`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                // Получаем информацию о ставках для каждой игры
                const gamesWithBetsPromises = data.games.map(async (game) => {
                    try {
                        const betsResponse = await fetch(`${API_URL}/api/user-freespins/bets?game_uuid=${game.uuid}&currency=${currentCurrency || 'RUB'}`, {
                            method: 'GET',
                            credentials: 'include',
                        });
                        const betsData = await betsResponse.json();
                        
                        // Если игра не поддерживает фриспины - возвращаем null
                        if (!betsData.success) {
                            return null;
                        }
                        
                        return {
                            ...game,
                            minBetAmount: betsData.min_amount || 1.0,
                            betInfo: betsData.recommended_bet
                        };
                    } catch (error) {
                        console.error(`Ошибка при получении ставок для игры ${game.name}:`, error);
                        return null; // Исключаем игры с ошибками
                    }
                });
                
                const gamesWithBetsResults = await Promise.all(gamesWithBetsPromises);
                // Фильтруем null значения (игры которые не поддерживают фриспины) и игры без картинок
                const gamesWithBets = gamesWithBetsResults.filter(game => {
                    if (game === null) return false;
                    // Проверяем наличие изображения
                    if (!game.image || game.image === '' || game.image === null) return false;
                    return true;
                });

                setAvailableGames(gamesWithBets);
                if (gamesWithBets.length > 0) {
                    setSelectedGame(gamesWithBets[0].uuid);
                    setCurrentGameIndex(0);
                }
            }
        } catch (error) {
            console.error('Ошибка при загрузке списка игр:', error);
        }
    };

    const fetchActiveFreespins = async () => {
        try {
            const response = await fetch(`${API_URL}/api/user-freespins/active`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                setActiveFreespins(data.freespins);
            }
        } catch (error) {
            console.error('Ошибка при загрузке активных фриспинов:', error);
        }
    };

    const fetchUserBalance = async () => {
        try {
            const response = await fetch(`${API_URL}/api/balance`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                setUserFreespins(data.freespins || 0);
            }
        } catch (error) {
            console.error('Ошибка при загрузке баланса:', error);
        }
    };

    const fetchWelcomePromocodes = async () => {
        try {
            const response = await fetch(`${API_URL}/api/promo/user-activated`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                // Фильтруем только WELCOME промокоды
                const welcomePromos = data.activatedPromoCodes.filter(promo =>
                    promo.code.startsWith('WELCOME')
                );

                // Создаем массив из 4 слотов для welcome промокодов
                const welcomeSlots = [
                    { code: 'WELCOME', status: 'inactive', id: null },
                    { code: 'WELCOME2', status: 'inactive', id: null },
                    { code: 'WELCOME3', status: 'inactive', id: null },
                    { code: 'WELCOME4', status: 'inactive', id: null }
                ];

                // Обновляем статусы активированных промокодов
                welcomePromos.forEach(promo => {
                    const slotIndex = welcomeSlots.findIndex(slot => slot.code === promo.code);
                    if (slotIndex !== -1) {
                        welcomeSlots[slotIndex] = {
                            ...welcomeSlots[slotIndex],
                            status: promo.is_used ? 'used' : 'active',
                            id: promo.id,
                            bonus_percent: promo.bonus_percent,
                            free_spins_count: promo.free_spins_count,
                            activated_at: promo.activated_at
                        };
                    }
                });

                setWelcomePromocodes(welcomeSlots);
            }
        } catch (error) {
            console.error('Ошибка при загрузке welcome промокодов:', error);
        }
    };

    // Функции для навигации по слайдеру фриспинов
    const goToPreviousGame = () => {
        if (availableGames.length > 0) {
            const newIndex = currentGameIndex > 0 ? currentGameIndex - 1 : availableGames.length - 1;
            setCurrentGameIndex(newIndex);
            setSelectedGame(availableGames[newIndex].uuid);
        }
    };

    const goToNextGame = () => {
        if (availableGames.length > 0) {
            const newIndex = currentGameIndex < availableGames.length - 1 ? currentGameIndex + 1 : 0;
            setCurrentGameIndex(newIndex);
            setSelectedGame(availableGames[newIndex].uuid);
        }
    };

    // Функции для слайдера активных фриспинов
    const scrollActiveFreespinsLeft = useCallback(() => {
        if (activeFreespinsSliderRef.current) {
            const containerWidth = activeFreespinsSliderRef.current.clientWidth;
            const scrollAmount = containerWidth * 0.8;
            const currentScroll = activeFreespinsSliderRef.current.scrollLeft;
            const newScroll = Math.max(0, currentScroll - scrollAmount);
            
            activeFreespinsSliderRef.current.scrollTo({
                left: newScroll,
                behavior: 'smooth'
            });
        }
    }, []);

    const scrollActiveFreespinsRight = useCallback(() => {
        if (activeFreespinsSliderRef.current) {
            const containerWidth = activeFreespinsSliderRef.current.clientWidth;
            const scrollAmount = containerWidth * 0.8;
            const currentScroll = activeFreespinsSliderRef.current.scrollLeft;
            const maxScroll = activeFreespinsSliderRef.current.scrollWidth - activeFreespinsSliderRef.current.clientWidth;
            const newScroll = Math.min(maxScroll, currentScroll + scrollAmount);
            
            activeFreespinsSliderRef.current.scrollTo({
                left: newScroll,
                behavior: 'smooth'
            });
        }
    }, []);

    const getGameImage = useCallback((freespin) => {
        if (!freespin.game_image || freespin.game_image === '') return null;
        if (freespin.game_image.startsWith('/')) {
            return `${API_URL}${freespin.game_image}`;
        }
        return freespin.game_image;
    }, []);

    const handleActivatePromoCode = async (e) => {
        e.preventDefault();
        if (!promoCode.trim()) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/promo/activate`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code: promoCode }),
            });
            const data = await response.json();

            if (data.success) {
                showNotification(data.message, "success");
                setPromoCode('');
                fetchActivatedPromoCodes();
                fetchWelcomePromocodes(); // Обновляем welcome промокоды
            } else {
                showNotification(data.message, "error");
            }
        } catch (error) {
            showNotification('Ошибка при активации промокода', "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAwardFreespins = async (e) => {
        e.preventDefault();
        if (!selectedGame || freespinAmount <= 0) return;

        if (freespinAmount > userFreespins) {
            showNotification(`Недостаточно фриспинов. Доступно: ${userFreespins}`, "error");
            return;
        }

        setFreespinLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/user-freespins/award`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    game_uuid: selectedGame,
                    freespins_count: freespinAmount,
                    bet_amount: 0.00, // Можно добавить выбор ставки
                    currency: currentCurrency || 'RUB'
                }),
            });
            const data = await response.json();

            if (data.success) {
                showNotification(data.message, "success");
                setFreespinAmount(10);
                fetchActiveFreespins();
                fetchUserBalance();
            } else {
                showNotification(data.message, "error");
            }
        } catch (error) {
            showNotification('Ошибка при начислении фриспинов', "error");
        } finally {
            setFreespinLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Универсальная функция для поиска и скролла к элементу
    const scrollToElement = (selectors, elementName) => {
        // Пробуем разные селекторы
        for (const selector of selectors) {
            const element = document.querySelector(selector);
            if (element) {
                try {
                    const yOffset = -120; // Отступ для navbar
                    const elementTop = element.getBoundingClientRect().top;
                    const y = elementTop + window.pageYOffset + yOffset;
                    window.scrollTo({
                        top: y,
                        behavior: 'smooth'
                    });
                    return true;
                } catch (error) {
                    console.log('Ошибка при расчете позиции, используем простой скролл:', error);
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return true;
                }
            }
        }

        console.log(`Элемент "${elementName}" не найден по селекторам:`, selectors);
        return false;
    };

    const handleBonusesScroll = () => {
        console.log('handleBonusesScroll вызвана, isAuth:', isAuth);
        if (!isAuth) {
            // Для неавторизованных - к информации о бонусах
            const element = document.querySelector('.howitworks-container') ||
                document.querySelector('h1[contains="Кешбек и бонусный счёт"]') ||
                document.querySelector('div[class*="howitworks"]');
            console.log('Найден элемент для неавторизованных:', element);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            // Для авторизованных - ищем блок с Rackeback счёт
            const racebackTitle = document.querySelector('.bonus-account-title');
            const bonusesRow = document.querySelector('.bonuses-row');
            const bonusBlock = document.querySelector('.bonuses-block-visual');

            console.log('racebackTitle найден:', racebackTitle);
            console.log('bonusesRow найден:', bonusesRow);
            console.log('bonusBlock найден:', bonusBlock);

            const targetElement = racebackTitle || bonusesRow || bonusBlock;
            if (targetElement) {
                console.log('Скроллим к:', targetElement);
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                console.log('Элементы не найдены для авторизованных, пробуем общий скролл');
                // Fallback - скроллим к любому первому блоку с классом bonus
                const anyBonusElement = document.querySelector('[class*="bonus"]') ||
                    document.querySelector('div[class*="bonuses"]') ||
                    document.querySelector('.bonuses-page-container > div:nth-child(2)');
                if (anyBonusElement) {
                    console.log('Fallback скролл к:', anyBonusElement);
                    anyBonusElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }
    };

    const handleWelcomeBonusesScroll = () => {
        console.log('handleWelcomeBonusesScroll вызвана');

        // Ищем различными способами
        const welcomeSection = document.querySelector('.welcome-bonuses-section');
        const welcomeTitle = document.querySelector('.welcome-bonuses-title');
        const welcomeH2 = document.querySelector('h2');

        console.log('welcomeSection найден:', welcomeSection);
        console.log('welcomeTitle найден:', welcomeTitle);
        console.log('welcomeH2 найден:', welcomeH2);

        // Дополнительно проверим по тексту
        const allH2 = document.querySelectorAll('h2');
        let welcomeByText = null;
        allH2.forEach(h2 => {
            if (h2.textContent && h2.textContent.includes('Приветственные бонусы')) {
                welcomeByText = h2;
            }
        });
        console.log('welcomeByText найден:', welcomeByText);

        const targetElement = welcomeSection || welcomeTitle || welcomeByText || welcomeH2;
        if (targetElement) {
            console.log('Скроллим к приветственным бонусам:', targetElement);
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            console.log('Элемент приветственных бонусов не найден, пробуем fallback');
            // Fallback - ищем любой элемент с текстом о приветственных бонусах
            const allDivs = document.querySelectorAll('div, section');
            let fallbackElement = null;
            allDivs.forEach(div => {
                if (div.textContent && div.textContent.includes('Приветственные')) {
                    fallbackElement = div;
                }
            });
            if (fallbackElement) {
                console.log('Fallback скролл к приветственным бонусам:', fallbackElement);
                fallbackElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                console.log('Даже fallback не найден для приветственных бонусов');
            }
        }
    };

    const handleBonus500Scroll = () => {
        // Ищем баннер Бонус +500%
        const imgs = document.querySelectorAll('img[alt*="Бонус +500%"], img[src*="promop1.png"]');
        for (const img of imgs) {
            const banner = img.closest('.bonuses-banner') || img.closest('button');
            if (banner) {
                console.log('Найден баннер Бонус +500%:', banner);
                banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
        }
        // Fallback - ищем контейнер баннеров
        scrollToElement(['.bonuses-banner-vertical'], 'Контейнер баннеров');
    };

    const handleCashbackScroll = () => {
        // Ищем баннер Кэшбек и бонусы
        const imgs = document.querySelectorAll('img[alt*="Кэшбек и бонусы"], img[src*="promop2.png"]');
        for (const img of imgs) {
            const banner = img.closest('.bonuses-banner') || img.closest('button');
            if (banner) {
                console.log('Найден баннер Кэшбек и бонусы:', banner);
                banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
        }
        // Fallback - ищем контейнер баннеров
        scrollToElement(['.bonuses-banner-vertical'], 'Контейнер баннеров');
    };

    const handleReferralScroll = () => {
        // Ищем баннер Реферальная программа
        const imgs = document.querySelectorAll('img[alt*="Реферальная программа"], img[src*="promop3.png"]');
        for (const img of imgs) {
            const banner = img.closest('.bonuses-banner') || img.closest('button');
            if (banner) {
                console.log('Найден баннер Реферальная программа:', banner);
                banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
        }
        // Fallback - ищем контейнер баннеров
        scrollToElement(['.bonuses-banner-vertical'], 'Контейнер баннеров');
    };

    const currencySymbol = getCurrencySymbol(currentCurrency);

    return (
        <div className="bonuses-page-container">


            {isAuth && (
                <div ref={bonusesRef} className="bonuses-row">
                    {/* Объединённый блок: Бонусный счёт + Ставки за 24 часа */}
                    <div className="bonuses-block-visual img-bg" style={{ position: 'relative' }}>
                        <img
                            src="/assets/banners/NewCashBackBanner.webp"
                            alt=""
                            className="bonus-bg-img"
                            aria-hidden="true"
                            draggable="false"
                        />
                        {/* Иконка информации */}
                        <button
                            className="info-icon-btn"
                            style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', cursor: 'pointer', zIndex: 2, padding: 0 }}
                            onClick={() => setShowDailyInfo(true)}
                            aria-label="Информация о кешбеке за 24 часа"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2" fill="#393a4d" />
                                <rect x="11" y="10" width="2" height="6" rx="1" fill="#fff" />
                                <rect x="11" y="7" width="2" height="2" rx="1" fill="#fff" />
                            </svg>
                        </button>
                        {showDailyInfo && (
                            <div className="info-modal" onClick={() => setShowDailyInfo(false)}>
                                <div className="info-modal-content" onClick={e => e.stopPropagation()}>
                                    <div className="info-modal-header">
                                        <span className="info-modal-icon">ℹ️</span>
                                        <h4>{t('bonuses.dailyCashbackInfo')}</h4>
                                    </div>
                                     <div className="info-modal-section">
                                         <div className="info-modal-subtitle">{t('bonuses.howItWorks')}</div>
                                         <div className="info-modal-formula">{t('bonuses.formula')}</div>
                                     </div>
                                     <div className="info-modal-section">
                                         <div className="info-modal-subtitle">{t('bonuses.example')}</div>
                                         <div className="info-modal-example">
                                             <b>{t('bonuses.yourBets24h')}</b> 10 000₽<br />
                                             <b>{t('bonuses.yourPersonalPercent')}</b> 4%<br />
                                             <b>{t('bonuses.availableToTransfer')}</b> 10 000 × 4% = <span className="info-modal-highlight">400₽</span><br />
                                             <small style={{ color: '#ccc' }}>Но не больше, чем есть на бонусном счёте</small>
                                         </div>
                                     </div>
                                     <div className="info-modal-section">
                                         <div className="info-modal-subtitle">{t('bonuses.features')}</div>
                                         <ul className="info-modal-list">
                                             <li>{t('bonuses.canCollectAnytime')}</li>
                                             <li>{t('bonuses.accountsAlreadyCollected')}</li>
                                             <li>{t('bonuses.limitedByBonusBalance')}</li>
                                             <li>{t('bonuses.transfersToBonusAccount')}</li>
                                         </ul>
                                     </div>
                                     <div className="info-modal-section" style={{ background: 'rgba(255, 87, 87, 0.1)', padding: '12px', borderRadius: '6px', marginTop: '12px' }}>
                                         <div className="info-modal-subtitle" style={{ color: '#ff5757' }}>{t('bonuses.bonusBurnWarning')}</div>
                                         <div style={{ color: '#ccc', fontSize: '13px', marginTop: '4px' }}>
                                             {t('bonuses.bonusBurnDescription')}
                                         </div>
                                     </div>
                                     <button className="close-info-btn" onClick={() => setShowDailyInfo(false)}>{t('bonuses.close')}</button>
                                </div>
                            </div>
                        )}
                        <div className="bonuses-block-title">{t('bonuses.bonusAccount')}</div>
                        <div className="bonus-account-amount" style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>
                            {getCurrencySymbol(currentCurrency)}{formatBalanceLocale(getCurrentBonusBalance())}
                        </div>

                        <div className="bonuses-progress-bar">
                            <div
                                className="bonuses-progress-fill"
                                style={{
                                    width: getCurrentBonusBalance() > 0 && bonusAmount > 0
                                        ? `${Math.max(Math.min((bonusAmount / getCurrentBonusBalance()) * 100, 100), 12)}%`
                                        : '12%'
                                }}
                            ></div>
                        </div>
                        <div className="bonuses-progress-labels">
                            <span className="current">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(bonusAmount)}</span>
                            <span className="target">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(getCurrentBonusBalance())}</span>
                        </div>

                        <div className="bonuses-block-divider"></div>

                        <div className="bonuses-block-row">
                            <span className="label">{t('bonuses.betsLast24h')}</span>
                            <span className="value">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(bonusTotalBets)}</span>
                        </div>
                        <div className="bonuses-block-row">
                            <span className="label">{t('bonuses.personalPercent')}</span>
                            <span className="value">{bonusPercent}%</span>
                        </div>
                        <div className="bonuses-block-row">
                            <span className="label">{t('bonuses.alreadyCollected24h')}</span>
                            <span className="value">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(bonusCollectedToday)}</span>
                        </div>
                        <div className="bonuses-block-row">
                            <span className="label">{t('bonuses.canCollectFromBonus')}</span>
                            <span className="value">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(bonusAmount)}</span>
                        </div>
                        <div className="bonuses-block-row">
                            <button
                                className="cashback-action-btn cashback-action-btn-full"
                                disabled={bonusAmount === 0 || collecting}
                                onClick={bonusAmount > 0 ? handleCollectBonusCashback : undefined}
                            >
                                {collecting ? t('bonuses.collecting') : t('bonuses.collect')}
                            </button>
                        </div>

                        {bonusToBurn > 0 && timeToNextCashback > 0 && (
                            <div className="bonuses-block-row bonuses-block-row-warning" style={{ marginTop: '8px', padding: '8px', background: 'rgba(255, 87, 87, 0.1)', borderRadius: '6px', fontSize: '12px' }}>
                                <div style={{ color: '#ccc' }}>
                                    {(() => {
                                        const now = new Date();
                                        const daysUntilFriday = (5 - now.getDay() + 7) % 7 || 7;
                                        const nextFriday = new Date(now.getTime() + daysUntilFriday * 24 * 60 * 60 * 1000);
                                        return nextFriday.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
                                     })()} {t('bonuses.burnDate')} {getCurrencySymbol(currentCurrency)}{formatBalanceLocale(bonusToBurn)} {t('bonuses.bonusesWord')}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Рейкбек */}
                    <div className="bonuses-block-visual img-bg" style={{ position: 'relative' }}>
                        <img
                            src="/assets/banners/NewCashBackBanner.webp"
                            alt=""
                            className="bonus-bg-img"
                            aria-hidden="true"
                            draggable="false"
                        />
                        <div className="bonuses-block-title">{t('bonuses.rakeback24h')}</div>
                        <div className="bonuses-block-row bonuses-block-row-info">
                            {t('bonuses.dailyRakebackDescription')}
                        </div>

                        <div className="bonuses-block-divider"></div>

                        <div className="bonuses-block-row">
                            <span className="label">{t('bonuses.rakeback24h')}</span>
                            <span className="value">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(rakebackAmount)}</span>
                        </div>
                        <div className="bonuses-block-row">
                            <button
                                className="cashback-action-btn cashback-action-btn-full"
                                disabled={!canCollectRakeback || rakebackAmount === 0 || collectingRakeback}
                                onClick={canCollectRakeback && rakebackAmount > 0 ? handleCollectRakeback : undefined}
                            >
                                {collectingRakeback ? t('bonuses.collecting') : canCollectRakeback ? t('bonuses.collect') : `${t('bonuses.collectThrough')} ${timerToNextRakeback}`}
                            </button>
                        </div>
                    </div>

                    {/* Еженедельный кешбек */}
                    <div className="bonuses-block-visual img-bg" style={{ position: 'relative' }}>
                        <img
                            src="/assets/banners/NewCashBackBanner.webp"
                            alt=""
                            className="bonus-bg-img"
                            aria-hidden="true"
                            draggable="false"
                        />
                        {/* Иконка информации */}
                        <button
                            className="info-icon-btn"
                            style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', cursor: 'pointer', zIndex: 2, padding: 0 }}
                            onClick={() => setShowWeeklyInfo(true)}
                            aria-label="Информация о кешбеке за 7 дней"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2" fill="#393a4d" />
                                <rect x="11" y="10" width="2" height="6" rx="1" fill="#fff" />
                                <rect x="11" y="7" width="2" height="2" rx="1" fill="#fff" />
                            </svg>
                        </button>
                        {showWeeklyInfo && (
                            <div className="info-modal" onClick={() => setShowWeeklyInfo(false)}>
                                <div className="info-modal-content" onClick={e => e.stopPropagation()}>
                                    <div className="info-modal-header">
                                        <span className="info-modal-icon">📅</span>
                                        <h4>{t('bonuses.weeklyCashbackInfo')}</h4>
                                    </div>
                                     <div className="info-modal-section">
                                         <div className="info-modal-subtitle">{t('bonuses.howCalculated')}</div>
                                         <div className="info-modal-formula">{t('bonuses.weeklyFormula')}</div>
                                         <small style={{ color: '#aaa', display: 'block', marginTop: '4px' }}>
                                             {t('bonuses.lossCalculation')}
                                         </small>
                                     </div>
                                     <div className="info-modal-section">
                                         <div className="info-modal-subtitle">{t('bonuses.example')}</div>
                                         <div className="info-modal-example">
                                             <b>{t('bonuses.yourBetsWeekly')}</b> 150 000₽<br />
                                             <b>{t('bonuses.yourWinsWeekly')}</b> 120 000₽<br />
                                             <b>{t('bonuses.yourLoss')}</b> 150 000 - 120 000 = 30 000₽<br />
                                             <b>{t('bonuses.yourPersonalPercent')}</b> 5%<br />
                                             <b>{t('bonuses.yourCashback')}</b> 30 000 × 5% = <span className="info-modal-highlight">1 500₽</span>
                                         </div>
                                     </div>
                                     <div className="info-modal-section">
                                         <div className="info-modal-subtitle">{t('bonuses.autoCredit')}</div>
                                         <ul className="info-modal-list">
                                             <li><b>{t('bonuses.everyFriday')}</b></li>
                                             <li>{t('bonuses.period')}</li>
                                             <li>{t('bonuses.creditedToMain')}</li>
                                             <li>{t('bonuses.weeklyLimit')}</li>
                                         </ul>
                                     </div>
                                     <button className="close-info-btn" onClick={() => setShowWeeklyInfo(false)}>{t('bonuses.close')}</button>
                                </div>
                            </div>
                        )}
                        <div className="bonuses-block-title">{t('bonuses.cashback7days')}</div>

                        {/* Прогресс бар до лимита */}
                        <div className="bonuses-progress-bar" style={{ marginTop: '12px' }}>
                            <div
                                className="bonuses-progress-fill"
                                style={{
                                    width: weeklyMaxCashback > 0
                                        ? `${Math.max(Math.min((weeklyCashback / weeklyMaxCashback) * 100, 100), 12)}%`
                                        : '12%'
                                }}
                            ></div>
                        </div>
                        <div className="bonuses-progress-labels">
                            <span className="current">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(weeklyCashback)}</span>
                            <span className="target">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(weeklyMaxCashback)}</span>
                        </div>


                        <div className="bonuses-block-divider"></div>
                         <div className="bonuses-block-row">
                             <span className="label">{t('bonuses.lostLast7days')}</span>
                             <span className="value">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(weeklyNetLoss)}</span>
                         </div>
                         <div className="bonuses-block-row">
                             <span className="label">{t('bonuses.personalPercent')}</span>
                             <span className="value">{weeklyPercent}%</span>
                         </div>
                         <div className="bonuses-block-row">
                             <span className="label">{t('bonuses.cashback7daysAmount')}</span>
                             <span className="value">{getCurrencySymbol(currentCurrency)}{formatBalanceLocale(weeklyCashback)}</span>
                         </div>


                        <div className="bonuses-block-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>

                            {timerToNextCashback && (
                                <div className="bonuses-timer-wrapper" style={{ marginTop: 8 }}>

                                    <div className="bonuses-timer-bubbles">
                                        {(() => {
                                            const parts = timerToNextCashback.split(':');
                                            return parts.map((part, idx) => (
                                                <React.Fragment key={idx}>
                                                    <span className="bonuses-timer-bubble">{part}</span>
                                                    {idx < parts.length - 1 && <span className="bonuses-timer-sep">:</span>}
                                                </React.Fragment>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}
                            <span className="micro-text">{t('bonuses.cashbackAutoCredit')}</span>
                        </div>

                    </div>
                </div>
            )}

            {/* Остальные разделы только для авторизованных пользователей */}
            {isAuth && (
                <div className="bonuses-row">

                    {/* Баланс фриспинов */}
                    <div className="bonuses-block-visual img-bg">
                        <img
                            src="/assets/banners/bonusbalance.webp"
                            alt=""
                            className="bonus-bg-img"
                            aria-hidden="true"
                            draggable="false"
                        />
                        <div className="bonus-account-content">
                            <div className="freespins-balance-label bonus-account-title">{t('bonuses.freespins')}</div>
                            <div className="freespins-balance-amount bonus-account-amount">{userFreespins} FS</div>
                            <div className="freespins-balance-desc bonus-account-desc">{t('bonuses.freespinsDescription')}</div>
                        </div>
                    </div>
                    {/* Блок начисления фриспинов */}
                    <div className="bonuses-block-visual img-bg">
                        <img
                            src="/assets/banners/NewChooseGame.webp"
                            alt=""
                            className="bonus-bg-img"
                            aria-hidden="true"
                            draggable="false"
                        />
                        <div className="bonus-account-content">
                            <div className="bonus-account-title">{t('bonuses.selectGameForFreespins')}</div>
                            {/* Слайдер с играми */}
                            {availableGames.length > 0 && availableGames[currentGameIndex] ? (
                                <div className="freespins-game-slider">
                                    <div className="freespins-game-image-fullwidth">
                                        <img
                                            src={availableGames[currentGameIndex].image}
                                            alt={availableGames[currentGameIndex].name}
                                            className="freespins-game-img"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className="freespins-game-placeholder" style={{ display: 'none' }}>
                                            <span>{availableGames[currentGameIndex].name}</span>
                                        </div>
                                        {availableGames.length > 1 && (
                                            <div className="freespins-slider-indicators-inside">
                                                {availableGames.map((_, index) => (
                                                    <button
                                                        key={index}
                                                        className={`freespins-slider-indicator ${index === currentGameIndex ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setCurrentGameIndex(index);
                                                            setSelectedGame(availableGames[index].uuid);
                                                        }}
                                                        disabled={freespinLoading}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <button
                                            className="freespins-slider-btn freespins-slider-btn-prev"
                                            onClick={goToPreviousGame}
                                            disabled={freespinLoading}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                        <button
                                            className="freespins-slider-btn freespins-slider-btn-next"
                                            onClick={goToNextGame}
                                            disabled={freespinLoading}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="freespins-game-info-row">
                                        <span className="freespins-game-title">{availableGames[currentGameIndex].name}</span>
                                        <span className="freespins-game-provider-label">{availableGames[currentGameIndex].provider}</span>
                                         <span className="freespins-game-bet-label">{t('bonuses.bet')} {availableGames[currentGameIndex].minBetAmount || 1.0} {currentCurrency || 'RUB'}</span>
                                    </div>
                                    <form onSubmit={handleAwardFreespins} className="freespins-form freespins-form-row">
                                        <input
                                            type="number"
                                            min="1"
                                            max={userFreespins}
                                            value={freespinAmount}
                                            onChange={(e) => setFreespinAmount(parseInt(e.target.value))}
                                            placeholder={t('bonuses.freespinsAmount')}
                                            disabled={freespinLoading}
                                            className="freespins-amount-input"
                                        />
                                        <button
                                            type="submit"
                                            disabled={freespinLoading || !selectedGame || freespinAmount <= 0 || freespinAmount > userFreespins || availableGames.length === 0}
                                            className="freespins-btn"
                                        >
                                            {freespinLoading ? t('bonuses.awarding') : t('bonuses.awardFreespins')}
                                        </button>
                                    </form>
                                </div>
                            ) : availableGames.length === 0 ? (
                                <div className="freespins-no-games">
                                    <p>{t('bonuses.noGamesAvailable')}</p>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Блок активации промокода */}
                    <div className="bonuses-block-visual img-bg">
                        <img
                            src="/assets/banners/NewPromosBanner.webp"
                            alt=""
                            className="bonus-bg-img"
                            aria-hidden="true"
                            draggable="false"
                        />
                        <div className="bonus-account-content">
                            <div className="bonus-account-title">{t('bonuses.promoCodes')}</div>

                            <form onSubmit={handleActivatePromoCode} className="promo-code-form">
                                <input
                                    type="text"
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                    placeholder={t('bonuses.enterPromoCode')}
                                    className="promo-code-input"
                                    disabled={loading}
                                />
                                <button
                                    type="submit"
                                    className="promo-code-btn"
                                    disabled={loading || !promoCode.trim()}
                                >
                                    {loading ? t('bonuses.activating') : t('bonuses.activatePromoCode')}
                                </button>
                            </form>
                            {activatedPromoCodes.length > 0 && (
                                <div className="activated-promo-codes-list">
                                    <h4>{t('bonuses.activatedPromoCodes')}</h4>
                                    <ul>
                                        {activatedPromoCodes.map((promo) => (
                                            <li key={promo.id} className="activated-promo-code-item">
                                                <span className="promo-code-value">{promo.code}</span>
                                                <span className={`promo-code-status ${promo.is_used ? 'used' : 'active'}`}>{promo.is_used ? t('bonuses.used') : t('bonuses.active')}</span>

                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Активные фриспины по играм - Слайдер */}
            {activeFreespins.length > 0 && (
                <div className="game-slider-container active-freespins-slider">
                    <div className="game-slider-header">
                        <h2 className="game-slider-title">
                            <img src="/assets/banners/icon4.webp" alt="Активные фриспины" className="game-slider-icon" />
                            {t('bonuses.activeFreespinsByGames')}
                        </h2>
                        <div className="game-slider-controls">
                            <button
                                className="slider-control-btn"
                                onClick={scrollActiveFreespinsLeft}
                                aria-label="Прокрутить влево"
                            >
                                <FaChevronLeft />
                            </button>
                            <button
                                className="slider-control-btn"
                                onClick={scrollActiveFreespinsRight}
                                aria-label="Прокрутить вправо"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                    
                    <div className="game-slider-wrapper">
                        <div className="game-slider" ref={activeFreespinsSliderRef}>
                            {activeFreespins
                                .filter(freespin => getGameImage(freespin)) // Фильтруем игры без картинок
                                .map((freespin) => (
                                <Link
                                    key={freespin.id}
                                    to={`/gis-game/${freespin.game_uuid}?name=${encodeURIComponent(freespin.game_name)}`}
                                    className="universal-game-card game-slider-card active-freespin-card"
                                    onMouseEnter={() => setHoveredFreespin(freespin.id)}
                                    onMouseLeave={() => setHoveredFreespin(null)}
                                >
                                    <div className="universal-game-image-container">
                                        <img
                                            src={getGameImage(freespin)}
                                            alt={freespin.game_name}
                                            className="universal-game-image"
                                            loading="lazy"
                                        />
                                        
                                        {/* Оверлей с Play иконкой */}
                                        {hoveredFreespin === freespin.id && (
                                            <div className="universal-game-play-overlay">
                                                <FaPlay className="play-icon" />
                                            </div>
                                        )}
                                        
                                        {/* Бейдж с информацией о фриспинах */}
                                        <div className="freespin-badge-info">
                                            <span className="freespin-count">{freespin.freespins_left}/{freespin.freespins_count} FS</span>
                                            {freespin.total_win > 0 && (
                                                <span className="freespin-win">+{parseFloat(freespin.total_win).toFixed(2)} {freespin.currency}</span>
                                            )}
                                        </div>
                                        
                                        {/* Информационная панель внизу карточки */}
                                        <div className="freespin-info-panel">
                                            <div className="freespin-game-title">
                                                <div className="freespin-game-name">{freespin.game_name}</div>
                                                <div className="freespin-game-provider">{freespin.game_provider}</div>
                                            </div>
                                            <div className="freespin-stats">
                                                <div className="freespin-info-item">
                                                    <span className="freespin-info-label">Осталось</span>
                                                    <span className="freespin-info-value">{freespin.freespins_left} FS</span>
                                                </div>
                                                {freespin.expires_at && (
                                                    <div className="freespin-info-item">
                                                        <span className="freespin-info-label">До</span>
                                                        <span className="freespin-info-value">{formatDate(freespin.expires_at)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Раздел Welcome бонусов */}
            <div ref={welcomeBonusesRef} className="welcome-bonuses-section">
                <h2 className="welcome-bonuses-title">{t('bonuses.welcomeBonuses')}</h2>
                <h4 className="welcome-bonuses-subtitle">{t('bonuses.welcomeBonusesSubtitle')}</h4>
                <div className="welcome-bonuses-row">
                    {[1, 2, 3, 4].map((num, idx) => {
                        const imgSrc = `/assets/banners/well${num}.webp`;
                        let bonus;
                        let showBtn = false;
                        let showLockedBtn = false;
                        let btnHandler = undefined;
                        if (isAuth && welcomePromocodes.length === 4) {
                            bonus = welcomePromocodes[idx];
                            showBtn = bonus.status === 'active';
                            showLockedBtn = !showBtn;
                            btnHandler = showBtn ? () => setShowPaymentModal(true) : undefined;
                        } else {
                            bonus = {
                                code: `WELCOME${num === 1 ? '' : num}`,
                                status: 'active',
                                bonus_percent: [200, 100, 75, 50][idx],
                                free_spins_count: [200, 50, 0, 0][idx],
                                description: [
                                    t('bonuses.firstDeposit'),
                                    t('bonuses.secondDeposit'),
                                    t('bonuses.thirdDeposit'),
                                    t('bonuses.fourthDeposit'),
                                ][idx],
                            };
                            showBtn = idx === 0;
                            showLockedBtn = idx !== 0;
                            btnHandler = () => setShowAuthModal(true);
                        }
                        return (
                            <div className="welcome-bonus-card active" key={bonus.code}>
                                <div className="welcome-bonus-info">
                                    <div className="welcome-bonus-title">{bonus.description || `${num}-й депозит`}</div>
                                    <div className="welcome-bonus-details">
                                        {bonus.bonus_percent > 0 && <span className="welcome-bonus-percent">+{bonus.bonus_percent}%</span>}
                                        {bonus.free_spins_count > 0 && <span className="welcome-bonus-fs">{bonus.free_spins_count} FS</span>}
                                    </div>
                                    <div className="welcome-bonus-status">
                                        {showBtn && <button className="welcome-bonus-btn" onClick={btnHandler}>{t('bonuses.getBonus')}</button>}
                                         {showLockedBtn && <button className="welcome-bonus-btn welcome-bonus-btn-locked" disabled>{t('bonuses.locked')}</button>}
                                    </div>
                                </div>
                                <img src={imgSrc} alt={bonus.code} className="welcome-bonus-img" />
                            </div>
                        );
                    })}
                    {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} isLoginType={false} />}
                </div>
            </div>

            {showPaymentModal && (
                <PaymentModal onClose={() => setShowPaymentModal(false)} />
            )}

        </div>
    );
};

export default BonusesPage; 